import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

interface CreateUserDialogProps {
  open: boolean;
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (email: string) => Promise<boolean>;
}

/**
 * ユーザー新規作成ダイアログ
 * メールアドレスを入力してCognitoにユーザーを作成する
 */
const CreateUserDialog: React.FC<CreateUserDialogProps> = ({
  open,
  isLoading,
  onClose,
  onSubmit,
}) => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const validateEmail = (value: string): boolean => {
    if (!value.trim()) {
      setEmailError('メールアドレスを入力してください');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setEmailError('有効なメールアドレスを入力してください');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleSubmit = async () => {
    if (!validateEmail(email)) return;
    const success = await onSubmit(email.trim());
    if (success) {
      setEmail('');
      setEmailError('');
    }
  };

  const handleClose = () => {
    setEmail('');
    setEmailError('');
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleSubmit();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonAddIcon />
        ユーザー作成
      </DialogTitle>

      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="メールアドレス"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) validateEmail(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          error={!!emailError}
          helperText={emailError || 'Cognitoの招待メールがユーザーに送信されます'}
          disabled={isLoading}
          sx={{ mt: 1 }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || !email.trim()}
          startIcon={isLoading ? <CircularProgress size={16} /> : <PersonAddIcon />}
        >
          作成
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUserDialog;
